/**
 * KanbanCard - Card individual do Kanban Board
 */
import { useMemo, useState } from 'react'
import ClassificationService from '@services/classification'
import Modal from '@components/shared/Modal'
import { prazoProcessNumber } from './processDocumentHelpers'

const PUBLICACAO_KEYS = ['publicacoes', 'publicações', 'publicacao', 'publicação', 'publications']

function parsePotentialJson(value) {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (!trimmed) return []

  if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
    try {
      return JSON.parse(trimmed)
    } catch {
      return trimmed
    }
  }

  if (trimmed.includes('\n')) {
    return trimmed
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  }

  return trimmed
}

function normalizePublicacoes(rawValue) {
  const parsed = parsePotentialJson(rawValue)
  if (!parsed) return []

  if (Array.isArray(parsed)) {
    return parsed
      .map((item, index) => {
        if (typeof item === 'string') return { id: `pub-${index}`, texto: item }
        if (item && typeof item === 'object') return { id: item.id || `pub-${index}`, ...item }
        return null
      })
      .filter(Boolean)
  }

  if (typeof parsed === 'object') return [{ id: parsed.id || 'pub-0', ...parsed }]
  return [{ id: 'pub-0', texto: String(parsed) }]
}

function getPublicacoesFromPrazo(prazo) {
  const fromPrazo = PUBLICACAO_KEYS.find((key) => prazo?.[key] != null)
  if (fromPrazo) return normalizePublicacoes(prazo[fromPrazo])

  const agenda = prazo?.agenda
  if (agenda && typeof agenda === 'object') {
    const agendaKey = PUBLICACAO_KEYS.find((key) => agenda[key] != null)
    if (agendaKey) return normalizePublicacoes(agenda[agendaKey])
  }

  return []
}

export default function KanbanCard({ prazo, onEdit, onDelete, canManageBoard = true, canViewProcessDocuments = false, onViewDocuments }) {
  const [showPublicacoesModal, setShowPublicacoesModal] = useState(false)

  const getDateValue = (...possibleValues) => {
    const value = possibleValues.find((item) => item != null && String(item).trim() !== '')
    if (!value) return ''
    return String(value).split('T')[0]
  }

  const area = prazo.area || ClassificationService.classificarArea(prazo.numeroProcesso, prazo.descricao)
  const areaTratada = area.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const areaNome = ClassificationService.getAreaName(areaTratada)
  const areaCor = ClassificationService.getAreaColor(areaTratada)
  const publicacoes = useMemo(() => getPublicacoesFromPrazo(prazo), [prazo])
  const numeroProcesso = prazoProcessNumber(prazo)
  const dataFatal = getDateValue(prazo.dataFatal, prazo.datafatal, prazo.data_fatal)
  const dataFatalInterna = getDateValue(prazo.dataFatalInterna, prazo.datafatalinterna, prazo.data_fatal_interna)
  const dataFatalRobo = getDateValue(prazo.dataFatalRobo, prazo.datafatalrobo, prazo.data_fatal_robo)
  const isPrazoDivergente = Boolean((dataFatalInterna && dataFatalRobo && dataFatalInterna !== dataFatalRobo) || dataFatalInterna == '' || dataFatal == '')

  const formatDate = (iso) => {
    if (!iso) return '—'
    const [y, m, d] = iso.split('T')[0].split('-')
    var date = `${d}/${m}/${y}`;

    if(date == '01/01/0001') return '—'

    return `${d}/${m}/${y}`
  }

  const getStatusInfo = () => {
    if (prazo.status === 'concluido') return { classe: 'status-concluido', texto: 'Concluído' }
    if (prazo.status === 'vermelho') return { classe: 'status-vencido', texto: 'FORA DO PROCEDIMENTO'}
    if (prazo.status === 'preto') return { classe: 'status-vencido', texto: `PRAZO FATAL: ${formatDate(dataFatal)}` }
    if (prazo.status === 'laranja') return { classe: 'status-atencao', texto: 'FORA DO PROCEDIMENTO' }
    return { classe: 'status-normal', texto: 'No Prazo' }
  }

  const statusInfo = getStatusInfo()

  const formatPublicacaoValue = (value) => {
    if (value == null || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <>
      <div
        className="kanban-card"
        draggable={canManageBoard}
        data-prazo-id={prazo.id}
        onDragStart={(e) => {
          if (!canManageBoard) return
          e.dataTransfer.setData('prazoId', prazo.id)
        }}
      >
        {/* Header */}
        <div className="card-header">
          <div className="card-header-tags">
            <span className="card-area" style={{ backgroundColor: areaCor }}>
              {areaNome}
            </span>
          </div>
          <div className="card-actions">
            {canViewProcessDocuments && (
              <button type="button" className="btn-card-action" draggable={false} disabled={!numeroProcesso}
                onPointerDown={(event) => event.stopPropagation()}
                onDragStart={(event) => { event.preventDefault(); event.stopPropagation() }}
                onClick={(event) => { event.stopPropagation(); if (numeroProcesso) onViewDocuments(numeroProcesso) }}
                title={numeroProcesso ? 'Ver documentos do processo' : 'Processo sem número CNJ válido'} aria-label="Ver documentos do processo">
                <i className="fas fa-folder-open" aria-hidden="true" />
              </button>
            )}
            <button type="button" className="btn-card-action" onClick={() => setShowPublicacoesModal(true)} title="Ver publicações" aria-label="Ver publicações">
              <i className="fas fa-newspaper" />
            </button>
            {canManageBoard && (
              <button type="button" className="btn-card-action" onClick={() => onEdit(prazo)} title="Editar" aria-label="Editar prazo">
                <i className="fas fa-edit" />
              </button>
            )}
            {canManageBoard && (
              <button type="button" className="btn-card-action delete" onClick={() => onDelete(prazo.id)} title="Excluir" aria-label="Excluir prazo">
                <i className="fas fa-trash" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="card-body">
          <h4 className="card-title">{prazo.titulo || prazo.numeroProcesso || 'Sem título'}</h4>
          <p className="card-description">{prazo.descricao || ''}</p>
          <div className="card-details">
            <div className="card-detail">
              <i className="fas fa-user" />
              <span>{prazo.responsavel || prazo.advogado || 'Não atribuído'}</span>
            </div>
            <div className="card-detail">
              <i className="fas fa-calendar" />
              <span>Vencimento Interno: {formatDate(prazo.dataFatalInterna)}</span>
            </div>
            <div className={`card-detail ${isPrazoDivergente ? 'badge-atrasado' : ''}`}>
              <i className="fas fa-robot" />
              <span>Vencimento (robô): {formatDate(dataFatalRobo)}</span>
            </div>
            <div className="card-detail">
              <i className="fas fa-calendar" />
              <span>Vencimento Judicial: {formatDate(dataFatal)}</span>
            </div>

            {prazo.prazoDias && (
              <div className="card-detail">
                <i className="fas fa-clock" />
                <span>{prazo.prazoDias} dias</span>
              </div>
            )}
            {formatDate(dataFatal) == '—' && <span className="card-flag-divergente">Fatal não identificado</span>}
          </div>
        </div>

        {/* Footer */}
        <div className="card-footer">
          <span className={`card-status ${statusInfo.classe}`}>{statusInfo.texto}</span>
        </div>
      </div>

      <Modal
        isOpen={showPublicacoesModal}
        onClose={() => setShowPublicacoesModal(false)}
        title="Publicações da agenda"
        subtitle={`${prazo.titulo || prazo.numeroProcesso || 'Prazo'} • ${publicacoes.length} publicação(ões)`}
        icon="fa-newspaper"
        size="large"
      >
        {publicacoes.length === 0 ? (
          <p className="publicacoes-empty">Nenhuma publicação vinculada a esta agenda.</p>
        ) : (
          <div className="publicacoes-list">
            {publicacoes.map((publicacao, index) => {
              const entries = Object.entries(publicacao).filter(([key]) => key !== 'id')
              return (
                <article key={publicacao.id || `pub-${index}`} className="publicacao-item">
                  <h4>Publicação {index + 1}</h4>
                  <div className="publicacao-grid">
                    {entries.map(([key, value]) => (
                      <div key={key} className="publicacao-field">
                        <span className="publicacao-label">{key}</span>
                        <span className="publicacao-value">{formatPublicacaoValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </Modal>
    </>
  )
}
